/**
 * Arbeidstid per person (plan 0026 P2 Arbeidstidsoversikt; until #275 the Kontroll tab Registreringer,
 * since then ?vis=oversikt, reached from Kontroll → Per ansatt and Rapporter's footer): one row per person with what they registered today and in the period, who is at
 * work, and what needs a manager. Works with or without Vaktplan; rows.ts decides what counts as
 * a fault in each mode. A row opens the work-time panel (WorkPanel): the raw records, manual
 * registration, corrections and their history. The §10-7 print, the Personalliste and its code
 * list are their own views (?vis=utskrift, ?vis=personalliste), reached from Rapporter.
 *
 * Addresses: ?vis=oversikt|personalliste|utskrift, with ?periode=dag|uke|maned|egen (egen with ?fra=&til=),
 * ?status=jobb|kontroll|ok. Any other /arbeidstid address (Oversikt → Dagen's ?dato=&ansatt=, Prosjekter's
 * ?mnd=&prosjekt=&godkjent=1) is a registration list, and opens Kontroll → Registreringer with the same scope
 * (kontrollAddress below, #275).
 */
import { errorText } from '@/api/client';
import { recordChanges, useShiftTypes, useShiftWrites, useShifts, useUsers } from '@/api/data';
import { useProjectWord, useProjects } from '@/api/projects';
import { useCan } from '@/auth/caps';
import { T } from '@/i18n';
import { breakPolicy, dayEntries, oslo, rulePerson, tenantRules, today } from '@/rules';
import { useTenant } from '@/shell/tenant';
import { Button, Empty, LoadError, Skeleton, toast } from '@/ui';
import { DateInput } from '@/ui/DateInput';
import { PeriodControl } from '@/ui/PeriodControl';
import { DN, MONTHS, avTone, fullName, initials, monthLabel, num } from '@/ui/format';
import { TabPanel, Status as Tag } from '@/ui/page';
import { addDays, dow, mondayOf } from '@timefly/core';
import { useState } from 'react';
import { Link, Navigate, useSearchParams } from 'react-router-dom';
import Personalliste from './arbeidstid/Personalliste';
import Register from './arbeidstid/Register';
import { WorkPanel } from './arbeidstid/WorkPanel';
import { rangeError, safeReturn } from './arbeidstid/period';
import { type ChangeNote, changeNotes, recordCsv } from './arbeidstid/record';
import { ISSUE_TEXT, type Issue, type Row, hm, rowsFor } from './arbeidstid/rows';
import { TimerHead } from './timeliste/TimerHead';
import { lastDayOf, monthOf } from './timeliste/summary';
import { type DraftShift, ShiftDrawer, newDraft } from './vaktplan/ShiftDrawer';

type Span = 'dag' | 'uke' | 'maned' | 'egen';
type Status = 'alle' | 'jobb' | 'kontroll' | 'ok';
const SPANS: { key: Span; label: string }[] = [
  { key: 'dag', label: 'I dag' },
  { key: 'uke', label: 'Uke' },
  { key: 'maned', label: 'Måned' },
  { key: 'egen', label: 'Egendefinert' },
];

function periodOf(span: Exclude<Span, 'egen'>, d: string) {
  if (span === 'dag') return { from: d, to: d };
  if (span === 'uke') return { from: mondayOf(d), to: addDays(mondayOf(d), 6) };
  return { from: `${monthOf(d)}-01`, to: lastDayOf(monthOf(d)) };
}
function step(span: Span, d: string, n: number) {
  if (span === 'dag') return addDays(d, n);
  if (span === 'uke') return addDays(d, 7 * n);
  const [y, m] = d.split('-').map(Number) as [number, number];
  const x = new Date(Date.UTC(y, m - 1 + n, 1));
  return `${x.getUTCFullYear()}-${String(x.getUTCMonth() + 1).padStart(2, '0')}-01`;
}
/**
 * The Kontroll → Registreringer address for an old /arbeidstid list address (#275): the month of ?dato= or
 * ?fra=, or ?mnd=; the week for ?periode=uke; the person, the project, approved only, and a return path.
 */
export function kontrollAddress(sp: URLSearchParams): string {
  const n = new URLSearchParams({ fane: 'registreringer' });
  const day = (v: string | null) =>
    v && /^\d{4}-\d{2}-\d{2}$/.test(v) && !Number.isNaN(Date.parse(v)) ? v : null;
  const d = day(sp.get('dato')) ?? day(sp.get('fra'));
  if (sp.get('periode') === 'uke' && d) n.set('uke', mondayOf(d));
  else if (d) n.set('mnd', d.slice(0, 7));
  else if (/^\d{4}-(0[1-9]|1[0-2])$/.test(sp.get('mnd') ?? '')) n.set('mnd', sp.get('mnd') as string);
  for (const k of ['ansatt', 'prosjekt']) if (sp.get(k)) n.set(k, sp.get(k) as string);
  if (sp.get('godkjent') === '1') n.set('vis', 'godkjent');
  const back = safeReturn(sp.get('tilbake')) ?? safeReturn(sp.get('fra'));
  if (back) n.set('tilbake', back);
  return `/timeliste?${n.toString()}`;
}
const dm = (d: string) => `${Number(d.slice(8, 10))}. ${MONTHS[Number(d.slice(5, 7)) - 1]}`;
function label(span: Span, from: string, to: string) {
  if (span === 'dag') return `${DN[dow(from)]} ${dm(from)} ${from.slice(0, 4)}`;
  if (span === 'maned') return monthLabel(from.slice(0, 7));
  if (from.slice(0, 4) !== to.slice(0, 4))
    return `${dm(from)} ${from.slice(0, 4)} – ${dm(to)} ${to.slice(0, 4)}`;
  return from.slice(5, 7) === to.slice(5, 7)
    ? `${Number(from.slice(8, 10))}.–${dm(to)} ${to.slice(0, 4)}`
    : `${dm(from)} – ${dm(to)} ${to.slice(0, 4)}`;
}
const periodWord: Record<Span, string> = {
  dag: 'i dag',
  uke: 'denne uken',
  maned: 'denne måneden',
  egen: 'i perioden',
};
const columnWord: Record<Span, string> = {
  dag: 'Dagen',
  uke: 'Uken',
  maned: 'Måneden',
  egen: 'Perioden',
};

export default function Arbeidstid() {
  const can = useCan();
  const { tenant, workspace } = useTenant();
  const scheduling = workspace.enabled.scheduling;
  const policy = breakPolicy(tenant);
  const [sp, setSp] = useSearchParams();
  const span = (SPANS.some((s) => s.key === sp.get('periode')) ? sp.get('periode') : 'uke') as Span;
  const status = (
    ['jobb', 'kontroll', 'ok'].includes(sp.get('status') ?? '') ? sp.get('status') : 'alle'
  ) as Status;
  const set = (k: string, v: string | null) =>
    setSp(
      (p) => {
        if (v == null) p.delete(k);
        else p.set(k, v);
        return p;
      },
      { replace: true },
    );
  const d0 = today();
  // ?dato= anchors the period (Oversikt → Dagen links a person's day here); stepping writes it
  const dato = sp.get('dato');
  // ?mnd=YYYY-MM anchors a month (Prosjekter links its totals this way)
  const mnd = sp.get('mnd');
  const anchor =
    dato && /^\d{4}-\d{2}-\d{2}$/.test(dato) && !Number.isNaN(Date.parse(dato))
      ? dato
      : mnd && /^\d{4}-\d{2}$/.test(mnd)
        ? `${mnd}-01`
        : d0;
  const setAnchor = (d: string) =>
    setSp(
      (p) => {
        p.delete('mnd');
        if (d === d0) p.delete('dato');
        else p.set('dato', d);
        return p;
      },
      { replace: true },
    );
  // ?ansatt= narrows the list to one person
  const ansatt = Number(sp.get('ansatt')) || null;
  // ?prosjekt=<id>|uten and ?godkjent=1: the hours behind a project total (Prosjekter)
  const prosjekt = sp.get('prosjekt');
  const onlyApproved = sp.get('godkjent') === '1';
  // ?tilbake=/path (or ?fra=/path, which a date never starts with): where "Tilbake" goes; a path
  // on this site only, never another host
  const back = safeReturn(sp.get('tilbake')) ?? safeReturn(sp.get('fra'));
  const projects = useProjects().data ?? [];
  const projectWord = useProjectWord();
  const [team, setTeam] = useState<string>('alle');
  const [q, setQ] = useState('');
  const fra = sp.get('fra');
  const til = sp.get('til');
  const bad = span === 'egen' ? rangeError(fra, til) : null;
  const { from, to } =
    span === 'egen'
      ? bad
        ? { from: d0, to: d0 }
        : { from: fra as string, to: til as string }
      : periodOf(span, anchor);

  const usersQ = useUsers();
  // the period, and today for the "I dag" column and who is at work when the period is elsewhere;
  // the day before because a night shift that started yesterday may still be running
  // and from the Monday of the period's first week, so the §10-7 record's running week counts the
  // days before the period (2026-09-23)
  const first = from < d0 ? from : d0;
  const earliest = mondayOf(first) < addDays(first, -1) ? mondayOf(first) : addDays(first, -1);
  // an invalid custom range asks for nothing: no totals from a period that is not one
  const shiftsQ = useShifts(earliest, to > d0 ? to : d0, !bad);
  const typesQ = useShiftTypes();
  const w = useShiftWrites();
  // the work-time panel for one person, and the shift drawer opened from it (step 3)
  const [openId, setOpenId] = useState<number | null>(null);
  const [edit, setEdit] = useState<DraftShift | null>(null);

  // the two views open from Rapporter (#173); someone who may not read pay opens them from Last ned here
  const backToReports = can('timerloenn.se') ? (
    <Link className="mlink tlback" to="/lonn">
      ‹ {T('Tilbake til Rapporter')}
    </Link>
  ) : (
    <Button ghost className="tlback" onClick={() => set('vis', 'oversikt')}>
      ‹ {T('Tilbake til oversikten')}
    </Button>
  );
  // the personalliste, bokføringsforskriften § 8-5-6 (plan 0027)
  if (sp.get('vis') === 'personalliste')
    return (
      <div className="timer">
        {backToReports}
        <Personalliste />
      </div>
    );
  if (sp.get('vis') === 'utskrift')
    return (
      <div className="timer">
        {backToReports}
        <Register />
      </div>
    );
  // a registration list address: Kontroll → Registreringer with the same scope (#275)
  if (sp.get('vis') !== 'oversikt') return <Navigate replace to={kontrollAddress(sp)} />;

  // AML §10-7: people who have left stay in the overview for the periods they worked (REC-10-7-1)
  const inPeriod = new Set(
    (shiftsQ.data?.shifts ?? [])
      .filter((s) => {
        const d = oslo(s.start_time)?.date ?? '';
        return d >= from && d <= to;
      })
      .map((s) => s.user_id),
  );
  const users = (usersQ.data ?? [])
    .filter((u) => u.active_status || inPeriod.has(u.id))
    .sort((a, b) => fullName(a).localeCompare(fullName(b), 'nb'));
  const teamsOf = (u: (typeof users)[number]) =>
    (u.teams ?? []).filter((t) => {
      const tid = (t as { tenant_id?: number }).tenant_id;
      return tid == null || tid === tenant?.id;
    });
  const teamNames = [...new Set(users.flatMap((u) => teamsOf(u).map((t) => t.name)))].sort((a, b) =>
    a.localeCompare(b, 'nb'),
  );
  // the project and approval scope picks the registrations that count; the §10-7 file keeps all
  const pool = (shiftsQ.data?.shifts ?? []).filter(
    (s) =>
      (!onlyApproved || s.is_approved) &&
      (prosjekt == null || (prosjekt === 'uten' ? s.project_id == null : s.project_id === Number(prosjekt))),
  );
  const rows = rowsFor(users, pool, {
    from,
    to,
    today: d0,
    now: Date.now(),
    scheduling,
    policy,
    tol: tenantRules(tenant).toleranceMin,
  });
  const needle = q.trim().toLowerCase();
  const scoped = rows.filter(
    (r) =>
      (ansatt == null || r.u.id === ansatt) &&
      (team === 'alle' || teamsOf(r.u).some((t) => t.name === team)) &&
      (!needle ||
        fullName(r.u).toLowerCase().includes(needle) ||
        teamsOf(r.u).some((t) => t.name.toLowerCase().includes(needle))),
  );
  const shown = scoped.filter((r) =>
    status === 'jobb'
      ? r.duty
      : status === 'kontroll'
        ? r.issues.length
        : status === 'ok'
          ? !r.issues.length
          : true,
  );
  const onDuty = scoped.filter((r) => r.duty).length;
  const registered = scoped.reduce((a, r) => a + r.period, 0);
  const withHours = scoped.filter((r) => r.period > 0).length;
  const issues = scoped.flatMap((r) => r.issues);
  const clean = scoped.filter((r) => !r.issues.length).length;
  const byIssue = (Object.keys(ISSUE_TEXT) as Issue[])
    .map((k) => [k, issues.filter((x) => x.issue === k).length] as const)
    .filter(([, n]) => n);

  const chip = (key: Status, text: string, n: number) => (
    <button
      type="button"
      aria-pressed={status === key}
      onClick={() => set('status', status === key || key === 'alle' ? null : key)}
    >
      {T(text)} <b className="tnum">{n}</b>
    </button>
  );
  const teamText = (r: Row) =>
    teamsOf(r.u)
      .map((t) => t.name)
      .join(', ') || '–';
  const openRow = (r: Row) => setOpenId(r.u.id);
  const unplanned = (typesQ.data ?? []).find((t) => /uten plan/i.test(t.name ?? '')) ?? typesQ.data?.[0];

  async function exportCsv() {
    // the §10-7 record for the period and the people on screen (step 4), with each registration's changes
    // (PROM-44) for a reader of the history; a history that cannot be read stops the file rather than leaving it out
    let notes = new Map<number, ChangeNote>();
    if (can('timeliste.se')) {
      try {
        notes = changeNotes(await recordChanges(from, to));
      } catch (e) {
        return toast(errorText(e));
      }
    }
    const a = document.createElement('a');
    a.href = URL.createObjectURL(
      new Blob(
        [
          recordCsv(
            scoped.map((r) => r.u),
            shiftsQ.data?.shifts ?? [],
            from,
            to,
            policy,
            notes,
          ),
        ],
        {
          type: 'text/csv;charset=utf-8',
        },
      ),
    );
    a.download = `arbeidstid-${from}-${to}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  }

  return (
    <div className="timer">
      {back && (
        <Link className="mlink tlback" to={back}>
          ‹ {T('Tilbake')}
        </Link>
      )}
      <TimerHead
        tab="ansatte"
        onAdd={() => setEdit({ ...newDraft(d0, null, unplanned), published: true })}
        reports={[
          {
            key: 'csv',
            label: T('Arbeidstid §10-7 (CSV)'),
            onSelect: exportCsv,
            disabled: !!bad || !shiftsQ.data,
          },
        ]}
      />
      <TabPanel tab="ansatte">
        <div className="mbar" role="toolbar" aria-label={T('Filter')}>
          <label className="mf">
            {T('Periode')}
            <select
              className="inp"
              aria-label={T('Periode')}
              value={span}
              onChange={(e) => {
                const v = e.target.value as Span;
                setSp(
                  (p) => {
                    if (v === 'uke') p.delete('periode');
                    else p.set('periode', v);
                    if (v === 'dag') p.delete('dato');
                    if (v === 'egen') {
                      // start from the period on screen, so the dates are never empty
                      p.set('fra', from);
                      p.set('til', to);
                    } else {
                      p.delete('fra');
                      p.delete('til');
                    }
                    return p;
                  },
                  { replace: true },
                );
              }}
            >
              {SPANS.map((s) => (
                <option key={s.key} value={s.key}>
                  {T(s.label)}
                </option>
              ))}
            </select>
          </label>
          {span === 'egen' ? (
            (['fra', 'til'] as const).map((k) => (
              <label className="mf" key={k} htmlFor={`timer-range-${k}`}>
                {k === 'fra' ? T('Fra') : T('Til')}
                <DateInput
                  id={`timer-range-${k}`}
                  className="inp"
                  value={(k === 'fra' ? fra : til) ?? ''}
                  aria-invalid={bad?.field === k || undefined}
                  aria-describedby={bad?.field === k ? 'timer-range-error' : undefined}
                  onValueChange={(value) => set(k, value || null)}
                />
                {bad?.field === k && (
                  <p className="tlperr" id="timer-range-error" role="alert">
                    {T(bad.text)}
                  </p>
                )}
              </label>
            ))
          ) : (
            <div className="mf">
              <span>{T(span === 'dag' ? 'Dag' : span === 'uke' ? 'Uke' : 'Måned')}</span>
              <PeriodControl
                value={anchor}
                onChange={setAnchor}
                prevLabel={T('Forrige')}
                nextLabel={T('Neste')}
                onPrev={() => setAnchor(step(span, anchor, -1))}
                onNext={() => setAnchor(step(span, anchor, 1))}
              >
                <span className="lbl">{label(span, from, to)}</span>
              </PeriodControl>
            </div>
          )}
          {teamNames.length > 0 && (
            <label className="mf">
              {T('Team')}
              <select
                className="inp"
                aria-label={T('Team')}
                value={team}
                onChange={(e) => setTeam(e.target.value)}
              >
                <option value="alle">{T('Alle team')}</option>
                {teamNames.map((t) => (
                  <option key={t}>{t}</option>
                ))}
              </select>
            </label>
          )}
          <label className="mf">
            {T('Søk')}
            <input
              className="inp"
              type="search"
              aria-label={T('Søk')}
              placeholder={T('Søk ansatt eller team')}
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
          </label>
        </div>

        {bad ? (
          <section className="mcard tlperr-box" data-testid="range-invalid">
            <h2>{T('Rett perioden for å se registreringene')}</h2>
            <p className="sub">{T('Listen, timene og rapportene vises når datoperioden er gyldig.')}</p>
          </section>
        ) : usersQ.isError || shiftsQ.isError ? (
          <LoadError
            what={T('arbeidstiden')}
            onRetry={() => {
              usersQ.refetch();
              shiftsQ.refetch();
            }}
          />
        ) : usersQ.isLoading || shiftsQ.isLoading ? (
          <Skeleton rows={6} />
        ) : (
          <>
            <div className="tlsumline">
              <span className="sub" data-testid="arbeidstid-sum">
                {num(registered, 1)} t {T('registrert')}{' '}
                {T(span === 'dag' && anchor !== d0 ? 'denne dagen' : periodWord[span])}, {T('for')}{' '}
                {withHours} {T('av')} {scoped.length} {T('ansatte')}, {label(span, from, to)}
              </span>
              {(prosjekt != null || onlyApproved) && (
                <span className="tlscope" data-testid="arbeidstid-scope">
                  {[
                    prosjekt == null
                      ? ''
                      : prosjekt === 'uten'
                        ? projectWord.none
                        : (projects.find((x) => x.id === Number(prosjekt))?.name ??
                          `${projectWord.one} ${prosjekt}`),
                    onlyApproved ? T('bare godkjente') : '',
                  ]
                    .filter(Boolean)
                    .join(', ')}
                </span>
              )}
              {(ansatt != null || prosjekt != null || onlyApproved) && (
                <button
                  type="button"
                  className="mlink"
                  onClick={() =>
                    setSp(
                      (p) => {
                        for (const k of ['ansatt', 'prosjekt', 'godkjent']) p.delete(k);
                        return p;
                      },
                      { replace: true },
                    )
                  }
                >
                  {T('Vis alle registreringer')}
                </button>
              )}
            </div>
            <fieldset className="mchips" aria-label={T('Status')} data-testid="arbeidstid-kpi">
              {chip('alle', 'Alle', scoped.length)}
              {chip('jobb', 'På jobb nå', onDuty)}
              {chip('kontroll', 'Må kontrolleres', issues.length)}
              {chip('ok', 'Ser bra ut', clean)}
            </fieldset>

            {status === 'kontroll' && byIssue.length > 0 && (
              <p className="tlsumline sub" data-testid="kontroll-sum">
                {issues.length} {T('må kontrolleres')}
                {byIssue.map(([k, n]) => `, ${n} ${T(ISSUE_TEXT[k]).toLowerCase()}`)}
              </p>
            )}

            <section className="mcard">
              {shown.length ? (
                <table className="mt cards acards">
                  <thead>
                    <tr>
                      <th>{T('Ansatt')}</th>
                      <th>{T('Team')}</th>
                      <th>{T('Status')}</th>
                      <th className="num">{T('I dag')}</th>
                      <th className="num">{T(columnWord[span])}</th>
                      <th>{status === 'kontroll' ? T('Problem') : T('Avvik')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {shown.map((r) => {
                      const first = r.issues[0];
                      return (
                        // biome-ignore lint/a11y/useKeyWithClickEvents: the row click is a pointer shortcut; the keyboard path is the name button
                        <tr
                          key={r.u.id}
                          className={openId === r.u.id ? 'sel' : ''}
                          data-testid="arbeidstid-row"
                          onClick={() => openRow(r)}
                          style={{ cursor: 'pointer' }}
                        >
                          <td>
                            <span className="mwho">
                              <span className={`av ${avTone(r.u.id)}`}>{initials(fullName(r.u))}</span>
                              <span>
                                <button
                                  type="button"
                                  className="mlink"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    openRow(r);
                                  }}
                                >
                                  {fullName(r.u)}
                                </button>
                                {!r.u.active_status && <small>{T('Sluttet')}</small>}
                              </span>
                            </span>
                          </td>
                          <td data-k={T('Team')}>{teamText(r)}</td>
                          <td data-k={T('Status')}>
                            {r.duty ? (
                              <Tag tone="ok">● {T('På jobb')}</Tag>
                            ) : (
                              <span className="sub">{T('Ikke på jobb')}</span>
                            )}
                          </td>
                          <td className="num tnum" data-k={T('I dag')}>
                            {hm(r.today)}
                          </td>
                          <td className="num tnum" data-k={T(columnWord[span])}>
                            {hm(r.period)}
                          </td>
                          <td className={first ? undefined : 'none'}>
                            {first ? (
                              // a missing clock-in is for attention, amber; red is for what blocks pay (#272 B-49)
                              <Tag
                                tone={
                                  first.issue === 'deviation' || first.issue === 'missing' ? 'warn' : 'dgr'
                                }
                              >
                                {T(ISSUE_TEXT[first.issue])}
                                {r.issues.length > 1 ? ` +${r.issues.length - 1}` : ''}
                              </Tag>
                            ) : (
                              <span className="sub">–</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              ) : (
                <Empty>
                  {status === 'kontroll'
                    ? T('Ingen registreringer må kontrolleres i perioden.')
                    : T('Ingen ansatte å vise.')}
                </Empty>
              )}
            </section>
          </>
        )}
        <WorkPanel
          key={openId ?? 0}
          row={rows.find((r) => r.u.id === openId) ?? null}
          shifts={pool.filter((x) => x.user_id === openId)}
          from={from}
          to={to}
          today={d0}
          now={Date.now()}
          types={typesQ.data ?? []}
          startTab={status === 'kontroll' ? 'eventuelt' : 'oversikt'}
          onClose={() => setOpenId(null)}
          onEdit={setEdit}
        />
      </TabPanel>
      <ShiftDrawer
        draft={edit}
        entries={(() => {
          const s = edit?.id != null ? shiftsQ.data?.shifts.find((x) => x.id === edit.id) : undefined;
          const u = users.find((x) => x.id === s?.user_id);
          return s && u ? dayEntries(s, rulePerson(u, tenant), tenantRules(tenant)) : [];
        })()}
        types={typesQ.data ?? []}
        users={users}
        onClose={() => setEdit(null)}
        onDelete={(id, reason) =>
          w.remove.mutateAsync({ id, ...(reason ? { reason } : {}) }).then(
            () => {
              toast(T('Registreringen er slettet'));
              setEdit(null);
            },
            (e) => toast(errorText(e)),
          )
        }
      />
    </div>
  );
}
